/**
 * AuctionResults.js
 *
 * Final standings, shown to everyone when the auction ends. Large enough for a projector.
 * - Winner, then every team that played, best first (see scoring.js)
 * - Each team's total (base score + bonuses), submitted items (the rest dimmed), spend, and any rules it missed
 * Everything is revealed now that the game is over, whatever the visibility settings were.
 *
 * The host's device saves the result to the SQL database once.
 */

import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import { getAuctionSettings } from "../assets/Rules";
import { getRoomLibrary } from "../library/libraries";
import { rankTeams } from "../helpers/scoring";
import { saveGameResult } from "../helpers/gameResults";
import { markResultSaved } from "../helpers/auctionLobby";

const AuctionResults = ({ room, teams, isAdmin, user, userTeam }) => {
    const settings = getAuctionSettings(room);
    const library = getRoomLibrary(room);
    const standings = rankTeams(teams, settings, library);
    const winners = standings.filter((entry) => entry.rank === 1);

    // --- Host saves the result to SQL, once (with a retry button if it fails) ---
    const [saveState, setSaveState] = useState(room.resultSaved ? "saved" : "idle");
    const hasTried = useRef(false);
    const saveResult = async () => {
        hasTried.current = true;
        setSaveState("saving");
        const saved = await saveGameResult(room, teams, settings, library, user);
        if (saved) await markResultSaved(room.id);
        setSaveState(saved ? "saved" : "failed");
    };
    useEffect(() => {
        if (isAdmin && !room.resultSaved && !hasTried.current) saveResult();
    }); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="w-full max-w-[110rem] mx-auto px-6 md:px-12 pt-24 pb-12 text-white">

            {/* --- Winner --- */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                className="text-center"
            >
                <p className="text-2xl md:text-3xl text-zinc-400">{library.name} · Final results</p>
                {winners.length > 0 ? (
                    <>
                        <Crown className="mx-auto mt-6 w-16 h-16 text-yellow" />
                        <div className="mt-2 font-extrabold leading-none text-[clamp(3rem,8vw,7rem)]">
                            {winners.map((entry) => entry.team.name).join(" & ")}
                        </div>
                        <div className="mt-4 text-3xl text-yellow font-bold">
                            {winners[0].total} {library.scoreAttribute}
                        </div>
                    </>
                ) : (
                    <div className="mt-6 text-5xl font-extrabold text-zinc-500">No teams played</div>
                )}
                <p className="mt-4 text-xl text-zinc-500">
                    {room.moviesSold || 0} of {library.items.length} sold
                </p>
            </motion.div>

            {/* --- Standings --- */}
            <div className="mt-14 space-y-6">
                {standings.map((entry, index) => {
                    const isUserTeam = entry.team.id === userTeam?.id;
                    return (
                        <motion.div
                            key={entry.team.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 + index * 0.15 }}
                            className={`rounded-3xl border bg-zinc-950 overflow-hidden ${isUserTeam ? "border-yellow" : "border-zinc-800"}`}
                        >
                            <div className={`h-3 ${entry.color.bg}`} />
                            <div className="p-6 md:p-8 flex flex-col lg:flex-row gap-6 lg:items-start">
                                {/* Rank + name */}
                                <div className="flex items-baseline gap-5 lg:w-1/3">
                                    <span className="text-6xl font-extrabold text-zinc-600">#{entry.rank}</span>
                                    <div>
                                        <div className="text-3xl md:text-4xl font-bold">
                                            {entry.team.name}
                                            {isUserTeam && <span className="ml-3 text-lg font-medium text-yellow">You</span>}
                                        </div>
                                        <div className="mt-1 text-xl text-zinc-400">
                                            {entry.team.members.map((member) => member.displayName || "Anonymous").join(", ")}
                                        </div>
                                    </div>
                                </div>

                                {/* Score */}
                                <div className="lg:w-1/4">
                                    <div className="text-5xl font-extrabold text-yellow">{entry.total}</div>
                                    <div className="mt-1 text-xl text-zinc-400">
                                        {entry.baseScore} {library.scoreAttribute}
                                        {entry.bonus > 0 && ` + ${entry.bonus} bonus`}
                                    </div>
                                    {entry.bonuses.map((bonus) => (
                                        <div key={bonus} className="text-lg text-green">{bonus}</div>
                                    ))}
                                    {!entry.metRules && (
                                        <div className="mt-2 text-lg text-coral">
                                            Didn't meet the rules: {entry.problems.join("; ")}
                                        </div>
                                    )}
                                </div>

                                {/* Items */}
                                <div className="flex-1">
                                    <div className="text-xl text-zinc-400 mb-2">
                                        {entry.submitted.length} of {entry.items.length} items submitted
                                        {entry.items.length > entry.submitted.length && (entry.chosenByTeam ? " (their pick)" : " (best set picked for them)")}
                                        {" · "}spent ₹{entry.spent}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {entry.items.map((item, itemIndex) => (
                                            <span key={itemIndex} className={`rounded-full border px-4 py-1 text-lg ${entry.submitted.includes(item) ? "border-zinc-600 text-zinc-200" : "border-zinc-900 text-zinc-600"}`}>
                                                {item.name} <span className="text-zinc-500">· {item.score}</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {isAdmin && (
                <p className="mt-10 text-center text-zinc-500">
                    {saveState === "saving" && "Saving result…"}
                    {saveState === "saved" && "Result saved to game history."}
                    {saveState === "failed" && (
                        <>
                            Couldn't save the result to game history.{" "}
                            <button onClick={saveResult} className="text-white underline">Try again</button>
                        </>
                    )}
                </p>
            )}
        </div>
    );
};

export default AuctionResults;
